/**
 * The guided script behind the "Chat with us" widget.
 *
 * Four questions, asked one at a time, then a review step before anything is
 * sent. The normalisers are forgiving of how people actually type — trailing
 * spaces, a phone number written with brackets and dashes, an email in mixed
 * case — so a visitor is never rejected over formatting.
 *
 * Deliberately free of React so the conversation logic can be tested directly.
 */

const NUMBER_WORDS = {
  zero: "0", oh: "0", o: "0", nought: "0",
  one: "1", won: "1",
  two: "2", to: "2", too: "2",
  three: "3",
  four: "4", for: "4", fore: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8", ate: "8",
  nine: "9",
  double: "DOUBLE", triple: "TRIPLE",
};

/** Expand "double seven" to "77" and map number words onto digits. */
export function wordsToDigits(input) {
  const tokens = String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9+\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

  const out = [];
  let repeat = 1;

  tokens.forEach((token) => {
    if (/^\+?\d+$/.test(token)) {
      const digits = token.replace("+", "");
      const plus = token.startsWith("+") ? "+" : "";
      out.push(plus + digits.repeat(repeat));
      repeat = 1;
      return;
    }

    const mapped = NUMBER_WORDS[token];
    if (mapped === "DOUBLE") {
      repeat = 2;
    } else if (mapped === "TRIPLE") {
      repeat = 3;
    } else if (mapped) {
      out.push(mapped.repeat(repeat));
      repeat = 1;
    } else if (token === "plus") {
      out.push("+");
      repeat = 1;
    }
  });

  return out.join("");
}

/**
 * Tidy an email address. Spaces are impossible in a real address, so the
 * written-out forms ("at", "dot") are safe to resolve — some people type an
 * address that way to dodge scrapers.
 */
export function normalizeEmail(input) {
  let value = String(input || "").toLowerCase().trim();

  const spoken = [
    [/\s+\b(at the rate of|at the rate|at sign|at)\b\s+/g, "@"],
    [/\s+\b(dot|point|period|full stop)\b\s+/g, "."],
    [/\s+\b(underscore|under score)\b\s+/g, "_"],
    [/\s+\b(hyphen|dash)\b\s+/g, "-"],
  ];
  spoken.forEach(([pattern, replacement]) => {
    value = value.replace(pattern, replacement);
  });

  return value.replace(/\s+/g, "").replace(/[.,;:!?]+$/, "");
}

/** Keep digits, allow a single leading "+" for an international prefix. */
export function normalizePhone(input) {
  const raw = String(input || "");
  const converted = /[a-z]/i.test(raw) ? wordsToDigits(raw) : raw;
  const plus = /^\s*\+/.test(raw) || converted.startsWith("+") ? "+" : "";
  return plus + converted.replace(/\D/g, "");
}

/** Title-case a name and drop trailing punctuation. */
export function normalizeName(input) {
  return String(input || "")
    .replace(/[.,;:!?]+$/, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** Collapse whitespace and sentence-case free text. */
export function normalizeText(input) {
  const value = String(input || "").trim().replace(/\s+/g, " ");
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** "Ashish Kumar" -> "Ashish". Used to keep the conversation personal. */
export function firstName(fullName) {
  return String(fullName || "").trim().split(/\s+/)[0] || "";
}

/**
 * The questions, in order. Kept short — every extra field is another chance
 * to lose someone before they finish.
 */
export const CHAT_FIELDS = [
  {
    id: "name",
    label: "Full name",
    prompt: "What's your full name?",
    retry: "Sorry, I didn't catch that. What's your full name?",
    hint: "First and last name",
    normalize: normalizeName,
    validate: (v) => (v.trim().length >= 2 ? "" : "I need at least a first name."),
    inputType: "text",
  },
  {
    id: "phone",
    label: "Phone number",
    prompt: "What's your phone number?",
    retry: "I need a few more digits than that. What's your phone number?",
    hint: "Digits only, country code optional",
    normalize: normalizePhone,
    validate: (v) => {
      const digits = v.replace(/\D/g, "");
      return digits.length >= 7 && digits.length <= 15
        ? ""
        : "A phone number should be between 7 and 15 digits.";
    },
    inputType: "tel",
  },
  {
    id: "email",
    label: "Email ID",
    prompt: "And your email ID?",
    retry: "That doesn't look like a complete email address. Could you check it?",
    hint: "you@company.com",
    normalize: normalizeEmail,
    validate: (v) => (EMAIL_PATTERN.test(v) ? "" : "That doesn't look like a valid email address."),
    inputType: "email",
  },
  {
    id: "query",
    label: "Query regarding",
    prompt: "What is your query regarding?",
    retry: "Take your time — what would you like the team to know?",
    hint: 'For example, "leased line for a new office"',
    normalize: normalizeText,
    validate: (v) => (v.trim().length >= 5 ? "" : "A sentence is enough to brief the team."),
    inputType: "text",
  },
];

export const ASSISTANT_NAME = "iService Assistant";

export const GREETING =
  "Hi, I'm the iService Assistant. I'll take a few details and pass them straight to the team.";

export const SIGN_OFF =
  "Thanks for sharing your details. Our live agent will get back to you shortly.";

/**
 * An acknowledgement between questions.
 *
 * Only the name earns one — a warm greeting once the person has introduced
 * themselves. Every later answer gets nothing, because thanking someone for
 * each field reads as filler and slows the exchange down.
 */
export function acknowledge(field, value) {
  if (field.id === "name") {
    return `Hi ${firstName(value)}, nice to meet you.`;
  }
  return "";
}

/** Joins an acknowledgement to the next question without a stray space. */
export function composeTurn(field, value, nextPrompt) {
  return [acknowledge(field, value), nextPrompt].filter(Boolean).join(" ");
}

/** Asks the visitor, by first name, to check what was captured. */
export function reviewPrompt(fullName) {
  const name = firstName(fullName);
  return name
    ? `${name}, could you check these details are right, then send them across?`
    : "Could you check these details are right, then send them across?";
}

/**
 * The payload handed to EmailJS — the same shape the written contact form
 * sends, so both channels arrive looking alike. The query becomes the message
 * body, and the subject line names who is asking.
 */
export function buildTemplateParams(answers, transcript) {
  const query = answers.query || "";
  const who = firstName(answers.name) || "a visitor";

  return {
    name: answers.name || "",
    email: answers.email || "",
    phone: answers.phone || "",
    location: "",
    subject: `Chat inquiry from ${who}`,
    message: query,
    query,
    from_name: answers.name || "",
    from_email: answers.email || "",
    reply_to: answers.email || "",
    channel: "Chat with us",
    transcript: (transcript || [])
      .map((line) => `${line.role === "bot" ? "Assistant" : "Visitor"}: ${line.text}`)
      .join("\n"),
  };
}
